import { Copy, Minus, Plus, Shuffle } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import type { DinnerTeam } from '@/types';

function shuffle<T>(arr: T[]): T[] {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

function maxTeamsPerMentor(teams: DinnerTeam[]): number {
  const counts = new Map<string, number>();
  for (const team of teams) {
    if (!team.mentorId) continue;
    counts.set(team.mentorId, (counts.get(team.mentorId) ?? 0) + 1);
  }
  let max = 0;
  for (const c of counts.values()) if (c > max) max = c;
  return max;
}

function distribute(teams: DinnerTeam[], slotCount: number): DinnerTeam[][] {
  const slots: DinnerTeam[][] = Array.from({ length: slotCount }, () => []);

  const NO_MENTOR_KEY = '__none__';
  const byMentor = new Map<string, DinnerTeam[]>();
  for (const team of teams) {
    const key = team.mentorId ?? NO_MENTOR_KEY;
    const list = byMentor.get(key) ?? [];
    list.push(team);
    byMentor.set(key, list);
  }

  const mentorGroups: DinnerTeam[][] = [];
  let noMentorGroup: DinnerTeam[] = [];
  for (const [key, group] of byMentor.entries()) {
    if (key === NO_MENTOR_KEY) noMentorGroup = group;
    else mentorGroups.push(group);
  }
  // Larger mentor groups first so their teams get fresh slots before slots fill.
  mentorGroups.sort((a, b) => b.length - a.length);

  // Place mentor-having teams in the earliest slots (slot 0, 1, ...).
  // Same mentor's teams go to different slots (so mentor can dine with each).
  for (const group of mentorGroups) {
    const shuffled = shuffle(group);
    for (let i = 0; i < shuffled.length; i++) {
      const target = i % slotCount;
      slots[target].push(shuffled[i]);
    }
  }

  // Distribute the no-mentor teams to balance overall slot sizes.
  const shuffledNone = shuffle(noMentorGroup);
  for (const team of shuffledNone) {
    let target = 0;
    for (let i = 1; i < slotCount; i++) {
      if (slots[i].length < slots[target].length) target = i;
    }
    slots[target].push(team);
  }

  return slots.map((slot) =>
    [...slot].sort((a, b) => {
      const m = (a.mentorName ?? 'zzz').localeCompare(b.mentorName ?? 'zzz');
      return m !== 0 ? m : (a.slug ?? '').localeCompare(b.slug ?? '');
    }),
  );
}

function buildMessage(slots: DinnerTeam[][]): string {
  const lines: string[] = ['🍽️ **Cena — turnos**', ''];
  slots.forEach((slot, i) => {
    lines.push(`**Turno ${i + 1}** (${slot.length} equipos)`);
    for (const team of slot) {
      const mentor = team.mentorName ? ` — Mentor: ${team.mentorName}` : '';
      lines.push(`• ${team.slug}${mentor}`);
    }
    lines.push('');
  });
  lines.push(
    '_Mentores se quedan en sus mesas — los equipos rotan por turno._',
  );
  return lines.join('\n');
}

export function DinnerRandomizer({ teams }: { teams: DinnerTeam[] }) {
  const [seed, setSeed] = useState(0);

  const autoSlots = useMemo(
    () => Math.max(2, maxTeamsPerMentor(teams)),
    [teams],
  );
  const [slotCount, setSlotCount] = useState(autoSlots);

  const slots = useMemo(
    // biome-ignore lint/correctness/useExhaustiveDependencies: `seed` is what re-rolls it
    () => distribute(teams, slotCount),
    [teams, slotCount, seed],
  );

  const message = useMemo(() => buildMessage(slots), [slots]);

  const copy = async () => {
    await navigator.clipboard.writeText(message);
    toast({ type: 'success', description: 'Mensaje copiado al portapapeles' });
  };

  if (teams.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          No hay equipos para distribuir.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={() => setSeed((s) => s + 1)}>
          <Shuffle className="mr-2 h-4 w-4" />
          Re-randomizar
        </Button>
        <Button variant="outline" onClick={copy}>
          <Copy className="mr-2 h-4 w-4" />
          Copiar mensaje
        </Button>
        <div className="ml-auto flex items-center gap-2 rounded-md border px-3 py-1.5">
          <span className="text-muted-foreground text-sm">Turnos</span>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => setSlotCount((n) => Math.max(2, n - 1))}
            disabled={slotCount <= 2}
          >
            <Minus className="h-3 w-3" />
          </Button>
          <span className="w-6 text-center font-medium tabular-nums">
            {slotCount}
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => setSlotCount((n) => Math.min(8, n + 1))}
            disabled={slotCount >= 8}
          >
            <Plus className="h-3 w-3" />
          </Button>
          <span className="text-muted-foreground text-xs">
            (auto: {autoSlots})
          </span>
        </div>
      </div>

      <div
        className="grid gap-4"
        style={{
          gridTemplateColumns: `repeat(${Math.min(slotCount, 3)}, minmax(0, 1fr))`,
        }}
      >
        {slots.map((slot, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: turno number is the slot identity
          <Card key={`turno-${i}`}>
            <CardHeader>
              <CardTitle>Turno {i + 1}</CardTitle>
              <CardDescription>{slot.length} equipos</CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-2">
              {slot.map((team) => (
                <div
                  key={team.id}
                  className="flex flex-col rounded-md border p-2"
                >
                  <span className="font-medium">{team.slug}</span>
                  {team.mentorName ? (
                    <span className="text-muted-foreground text-xs">
                      {team.mentorName}
                    </span>
                  ) : (
                    <span className="text-muted-foreground text-xs italic">
                      Sin mentor
                    </span>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Mensaje (Discord / Slides)</CardTitle>
          <CardDescription>
            Copia este texto y pégalo en Discord o en una slide.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <pre className="whitespace-pre-wrap rounded-md bg-muted p-4 font-mono text-sm">
            {message}
          </pre>
        </CardContent>
      </Card>
    </div>
  );
}
